import type { CorroborationGap, Event } from '../lib/types';
import { formatDate } from '../lib/format';
import { Pill } from './Pill';

/**
 * What each gap asks the reader to go and find.
 *
 * Written out rather than run through `label()` because the useful string is an
 * instruction, not a noun. The middle one used to be shown for every event short
 * of `A`, including events already carrying several independent reports — where
 * no number of further independent sources can change the grade, and only an
 * official record will. Acting on that prompt produced no movement and no
 * explanation of why.
 */
const NEEDED: Record<CorroborationGap, string> = {
  credible_source: 'Needs a credible source',
  independent_source: 'Needs an independent source',
  primary_record: 'Needs a primary record',
};

/**
 * Shows how well evidenced an event is alongside what it claims.
 *
 * The grade is derived from the sources cited, so a bare `A` reads as a verdict
 * with nothing behind it. Showing the number of independent organisations next
 * to the letter keeps the reasoning visible, and an event still short of
 * corroboration says what would settle it rather than that it is unsettled.
 */
export function EventRow({ event }: { event: Event }) {
  const attested = `${event.evidenceGrade} · ${event.independentSources} independent`;
  return (
    <article className="event-row">
      <div className="event-row__marks">
        <Pill value={event.eventType} tone="accent" />
        <Pill value={attested} tone={event.corroborationGap ? 'warning' : 'positive'} />
      </div>
      <div className="event-row__body">
        <strong>{event.companyName ?? 'Company signal'}</strong>
        <span>{event.title}</span>
        {event.summary && <small>{event.summary}</small>}
        {event.corroborationGap && <small className="muted">{NEEDED[event.corroborationGap]}</small>}
      </div>
      <time>{formatDate(event.eventDate ?? event.observedAt)}</time>
    </article>
  );
}
